import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  createOrderService,
  getCategoriesService,
  getOrderByIdService,
  getOrdersService,
  getProductByIdService,
  getProductsService,
} from "../../api/shopService";

/* ---------------- PRODUCTS ---------------- */

export const fetchProducts = createAsyncThunk(
  "shop/fetchProducts",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getProductsService();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  },
);

/* ---------------- CATEGORIES ---------------- */

export const fetchCategories = createAsyncThunk(
  "shop/fetchCategories",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getCategoriesService();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  },
);

/* ---------------- ORDERS ---------------- */

export const fetchOrders = createAsyncThunk(
  "shop/fetchOrders",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getOrdersService();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  },
);

/* ---------------- PRODUCT DETAIL ---------------- */

export const fetchProductById = createAsyncThunk(
  "shop/fetchProductById",
  async (productId, { rejectWithValue }) => {
    try {
      const response = await getProductByIdService(productId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  },
);
export const createOrder = createAsyncThunk(
  "shop/createOrder",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await createOrderService(payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  },
);
/* ---------------- SINGLE ORDER ---------------- */

export const fetchOrderById = createAsyncThunk(
  "shop/fetchOrderById",
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await getOrderByIdService(orderId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  },
);

/* ---------------- SLICE ---------------- */

const shopSlice = createSlice({
  name: "shop",
  initialState: {
    products: [],
    categories: [],
    orders: [],
    cartItems: [],
    selectedOrder: null,
    selectedProduct: null,
    loading: false,
    error: null,
  },

  reducers: {
    addToCart: (state, action) => {
      const item = action.payload;

      const existing = state.cartItems.find(
        (product) => product.id === item.id,
      );

      if (existing) {
        existing.quantity += 1;
      } else {
        state.cartItems.push({ ...item, quantity: 1 });
      }
    },

    removeFromCart: (state, action) => {
      state.cartItems = state.cartItems.filter(
        (item) => item.id !== action.payload,
      );
    },

    updateCartQuantity: (state, action) => {
      const { id, quantity } = action.payload;

      const item = state.cartItems.find((product) => product.id === id);

      if (item) {
        item.quantity = quantity > 1 ? quantity : 1;
      }
    },

    clearCart: (state) => {
      state.cartItems = [];
    },
  },

  extraReducers: (builder) => {
    builder

      /* PRODUCTS */

      .addCase(fetchProducts.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.products = action.payload;
        state.loading = false;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      /* PRODUCT DETAIL */

      .addCase(fetchProductById.pending, (state) => {
        state.loading = true;
      })

      .addCase(fetchProductById.fulfilled, (state, action) => {
        state.selectedProduct = action.payload;
        state.loading = false;
      })

      .addCase(fetchProductById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      /* CATEGORIES */

      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.categories = action.payload;
      })
      .addCase(createOrder.pending, (state) => {
        state.loading = true;
      })
      .addCase(createOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.orders.push(action.payload);
        state.cartItems = []; // clear cart after order
      })
      .addCase(createOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      /* ORDERS */

      .addCase(fetchOrders.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.orders = action.payload;
        state.loading = false;
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      /* SINGLE ORDER */

      .addCase(fetchOrderById.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchOrderById.fulfilled, (state, action) => {
        state.selectedOrder = action.payload;
        state.loading = false;
      })
      .addCase(fetchOrderById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});
export const {
  addToCart,
  removeFromCart,
  updateCartQuantity,
  clearCart,
} = shopSlice.actions;
export default shopSlice.reducer;
